/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Stack,
  TextField,
  Button,
  Input,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { changeQuantity, changePartialPremium } from '../../../../../actions/cart';
import {
  aplPaymentType,
  loanPaymentType,
  outstandingGroupName,
  advanceGroupName,
  premiumPaymentType,
  customPaymentType,
} from '../../../../../utils/variables';
import { useFeatureToggle, useVariables, useGTMDataLayer } from '../../../../../hooks';
import { formatValue } from '../../../helpers';
import styles from './style';
import { toTitleCase } from '../../../../../utils/functions';
import {
  removeFromCartEvent,
  updateCartQuantityEvent,
} from '../../../../../utils/variables/events';
import { CustomTooltip } from '../../../../../componentsV2/common';

const slugify = require('slugify');

const useStyles = makeStyles(styles);

const symbolsArr = ['e', 'E', '+', '-'];

export const calculateCartItemLimits = ({ container, item, variables, paymentType }) => {
  const quantityOptions = [];
  const isLoanPaymentType =
    paymentType === loanPaymentType || paymentType === aplPaymentType;
  const businessUnit = container?.businessUnit;

  const isOutstandingAmount = item?.isOutstandingAmount;
  let partialPaymentLimits = variables?.partialPaymentLimits;
  if (paymentType === customPaymentType) {
    partialPaymentLimits = variables?.customPartialPaymentLimits;
  } else if (isOutstandingAmount) {
    partialPaymentLimits = variables?.outstandingPartialPaymentLimits;
  }

  const finalPartialPaymentLimits =
    (partialPaymentLimits && partialPaymentLimits[businessUnit]) || {};
  const paymentGroupsText = variables?.paymentGroupsText;
  const {
    minimumPremiumsDue = 0,
    premiumIntervals = 1,
    maximumAllowed,
    minimumAllowed,
    minimumLoanAmountAllowed,
    maxDynamicKey,
  } = finalPartialPaymentLimits;

  const totalOutstandingOptions = item.premiumsDue || 0;
  // console.log('totalOutstandingOptions: ', totalOutstandingOptions);
  // console.log('minimumPremiumsDue: ', minimumPremiumsDue);
  // console.log('item.premiumsDue: ', item.premiumsDue);
  const advancedPayAllowed = Number(item?.advancedPayAllowed) || 0;

  let least = 0;
  let highest = 0;

  if (paymentType === premiumPaymentType) {
    least = (item?.modalPremium || item?.renewalPremium) * minimumPremiumsDue;
    const premiumsDue = item?.premiumsDue || 0;
    highest =
      (premiumsDue + advancedPayAllowed) * (item?.modalPremium || item?.renewalPremium);
  }
  if (paymentType === customPaymentType) {
    least = minimumAllowed;
    highest = maximumAllowed;
  }
  if (item?.isFirstPremium) {
    least = item?.subTotal;
    highest = item?.subTotal;
  }
  if (isLoanPaymentType) {
    highest = item?.fullOutstandingAmount;
  }
  if (isOutstandingAmount) {
    highest = item?.fullOutstandingAmount;
  }
  if (isOutstandingAmount) {
    least = item?.fullOutstandingAmount * minimumPremiumsDue;
  }
  if (maximumAllowed && maximumAllowed < highest) {
    highest = maximumAllowed;
  }
  if (minimumAllowed && minimumAllowed > least) {
    least = minimumAllowed;
  }
  if (minimumLoanAmountAllowed && isLoanPaymentType) {
    least = minimumLoanAmountAllowed;
  }
  if (maxDynamicKey) {
    const maxValue = item[maxDynamicKey];
    if (maxValue) {
      highest = maxValue;
      if (least && maxValue < least) {
        least = maxValue;
      }
    }
  }

  least = parseFloat(least);
  least = Math.floor(least * 100) / 100;
  highest = parseFloat(highest);
  highest = Math.ceil(highest * 100) / 100;
  // console.log('totalQuantityOptions: ', totalQuantityOptions);
  // console.log('item.premiumsDue: ', item.premiumsDue);
  // const totalDue = totalPremiumDue * item.modalPremium || 0;
  if (item) {
    const unitAmount = item?.modalPremium || item?.renewalPremium;

    for (
      let i = minimumPremiumsDue;
      i <= totalOutstandingOptions;
      i += premiumIntervals
    ) {
      const quantity = i;
      const total = unitAmount * quantity || 0;
      const newTotal = (Math.round(parseFloat(total) * 100) / 100).toFixed(2);
      if (quantity > 0) {
        quantityOptions.push({
          value: quantity,
          amount: newTotal,
          label: newTotal,
          groupDisplayName: paymentGroupsText?.[outstandingGroupName],
        });
      }
    }

    if (advancedPayAllowed) {
      for (let j = 1; j <= advancedPayAllowed; j += 1) {
        const paymentQuantity = quantityOptions.length;
        const value = paymentQuantity + 1;
        const total = value * unitAmount || 0;
        const newTotal = (Math.round(parseFloat(total) * 100) / 100).toFixed(2);
        quantityOptions.push({
          value,
          amount: newTotal,
          label: newTotal,
          groupDisplayName: paymentGroupsText?.[advanceGroupName],
        });
      }
    }
  }

  return {
    least,
    highest,
    quantityOptions,
    partialPaymentLimits: finalPartialPaymentLimits,
  };
};

export const checkInputValue = (newValue, least, highest) => {
  const invalidMessage = highest
    ? `Minimum accepted value is ${formatValue(
        least,
      )}, while the highest accepted value is ${formatValue(highest)}`
    : `Minimum accepted value is ${formatValue(least)}`;

  const isValid =
    (newValue >= least && highest && newValue <= highest) ||
    (newValue >= least && !highest);

  return {
    isValid,
    invalidMessage,
  };
};
export default function ChangeAmount({
  item,
  container,
  disabled,
  disabledDropdown,
  onChange,
  label,
  placeholder,
  hideUpdateButton,
  tooltip,
  isRequiredError,
  inputValue,
  setInputValue,
  setSelectValue,
  selectValue,
  initialPaymentType,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const inputRef = React.useRef();
  const storeGtmDataLayer = useGTMDataLayer();
  const [focus, setFocus] = React.useState(false);

  const variables = useVariables();
  // console.log('focus: ', focus);
  // console.log('item: ', item);
  // console.log('selectValue: ', selectValue);
  const [internalTimer, setInternalTimer] = useState(null);
  const paymentType = initialPaymentType || item?.paymentType;
  const loading = false;
  const isPremiumPaymentType = paymentType === premiumPaymentType;
  const isLoanPaymentType =
    paymentType === loanPaymentType || paymentType === aplPaymentType;
  const businessUnit = container?.businessUnit;
  const doFeatureToggle = useFeatureToggle();
  const onlyAllowFixedPartialPremiumAmount =
    isPremiumPaymentType &&
    doFeatureToggle(`onlyAllowFixedPartialPremiumAmount.${item?.lob}`);
  const onlyAllowFixedPartialLoanAmount =
    isLoanPaymentType && doFeatureToggle(`onlyAllowFixedPartialLoanAmount.${item?.lob}`);
  const isGroupByEnabled = doFeatureToggle(`paymentGroups`);

  const { least, highest, quantityOptions } = calculateCartItemLimits({
    container,
    item,
    variables,
    paymentType,
  });

  const checkValidity = checkInputValue(inputValue, least, highest);
  const defaultInputErrorMsg =
    isRequiredError && !checkValidity?.isValid && checkValidity?.invalidMessage;
  const [inputError, setInputError] = useState(defaultInputErrorMsg);

  const handleSelect = (event, label, groupName) => {
    const newAmount = label;
    // const newItem = { ...item, quantity: newQty };
    // const policyId = item?.policyId;
    const cartContainerId = container?._id;
    const cartId = item?._id;
    const formatInputValue =
      newAmount && (Math.round(parseFloat(newAmount) * 100) / 100).toFixed(2);
    const params = {
      cartId,
      cartContainerId,
      amount: formatInputValue,
    };
    if (onChange) {
      setFocus(false);
      setInputError(null);
      return onChange(params);
    }
    return dispatch(changePartialPremium.changePartialPremiumAction(params)).then(() => {
      setInputError(null);

      const status =
        formatInputValue === item?.subTotal
          ? 0
          : formatInputValue - item?.subTotal > 0
          ? 1
          : -1;

      const items = [
        {
          item_id: item?.productCode,
          item_name: item?.productName,
          price: formatInputValue - item?.subTotal,
          quantity: item?.quantity,
        },
      ];

      const slug =
        item?.productName &&
        slugify(item?.productName, {
          lower: true, // Convert to lowercase
          strict: true, // Remove special characters
        });

      storeGtmDataLayer({
        event: updateCartQuantityEvent,
        lob: item?.lob,
        amount: formatInputValue - item?.subTotal,
        country: container?.country,
        currency: container?.currency,
        paymentType,
        items,
        action: 'dropdown',
        status,
        planId: item?.productCode,
        policySlug: slug,
      });
    });
  };

  const saveEditValue = () => {
    const formatInputValue =
      inputValue && (Math.round(parseFloat(inputValue) * 100) / 100).toFixed(2);
    if (!formatInputValue) {
      setInputError(`This field is required`);
      return;
    }
    const validResponse = checkInputValue(formatInputValue, least, highest);
    if (!validResponse?.isValid) {
      setInputError(validResponse?.invalidMessage);
      return;
    }
    const params = {
      cartContainerId: container?._id,
      cartId: item?._id,
      amount: formatInputValue,
    };
    dispatch(changePartialPremium.changePartialPremiumAction(params)).then(() => {
      setInputError(null);

      const status =
        formatInputValue === item?.subTotal
          ? 0
          : formatInputValue - item?.subTotal > 0
          ? 1
          : -1;

      const items = [
        {
          item_id: item?.productCode,
          item_name: item?.productName,
          price: formatInputValue - item?.subTotal,
          quantity: item?.quantity,
        },
      ];

      const slug =
        item?.productName &&
        slugify(item?.productName, {
          lower: true, // Convert to lowercase
          strict: true, // Remove special characters
        });

      storeGtmDataLayer({
        event: updateCartQuantityEvent,
        lob: item?.lob,
        amount: formatInputValue - item?.subTotal,
        country: container?.country,
        currency: container?.currency,
        paymentType,
        items,
        action: 'input',
        status,
        planId: item?.productCode,
        policySlug: slug,
      });
    });
  };

  const renderOption = (element, option) => {
    // console.log('option: ', option);
    const handleClick = () => {
      // console.log('newValue: ', option);
      if (option) {
        setSelectValue(option?.label);
        setInputValue(option?.label);
        handleSelect(null, option?.label, option?.groupDisplayName);
      }
    };

    return (
      <Box
        display="flex"
        flexDirection="column"
        sx={{ m: 1, ml: 2, p: 1, textDecoration: 'none', minWidth: '10rem' }}
        onClick={handleClick}
      >
        <Grid container spacing={1} justifyContent="space-between">
          <Grid item xs={6}>
            {option?.label && (
              <Typography variant="body1" component="p" sx={{ color: 'text.primary' }}>
                {option?.label && `${formatValue(option.label)}`}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            {option?.value && (
              <Typography variant="body2" component="p" sx={{ color: 'text.label' }}>
                {option?.value &&
                  `(${option.value} ${option.value === 1 ? 'premium' : 'premiums'})`}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  };

  const tempChange = value => {
    setInputValue(value);
    clearTimeout(internalTimer);
    setInternalTimer(
      setTimeout(() => {
        if (onChange) {
          const checkResponse = checkInputValue(value, least, highest);
          if (!checkResponse?.isValid && isRequiredError) {
            setInputError(checkResponse?.invalidMessage);
          } else {
            setInputError(null);
          }
          return onChange({ amount: value });
        }
      }, 1300),
    );
  };

  const endAdornment = tooltip && <CustomTooltip title={tooltip} />;

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Autocomplete
        open={disabledDropdown ? false : focus}
        freeSolo
        autoHighlight
        disabled={disabled}
        loading={loading}
        disableClearable
        classes={{
          paper: classes.paper,
          root: classes.amountField,
        }}
        options={quantityOptions}
        groupBy={
          isGroupByEnabled ? option => toTitleCase(option.groupDisplayName) : undefined
        }
        value={selectValue?.toString() || ''}
        inputValue={inputValue?.toString() || ''}
        filterOptions={options => options.filter(option => option.label)}
        onInputChange={(event, newInputValue) => {
          if (
            focus &&
            !onlyAllowFixedPartialPremiumAmount &&
            !onlyAllowFixedPartialLoanAmount
          ) {
            // console.log('newInputValue: ', newInputValue);
            // console.log('event: ', event);
            // handleChange(event, newInputValue);
            tempChange(newInputValue);
          }
        }}
        isOptionEqualToValue={(option, optionTwo) => option.value === optionTwo.value}
        renderOption={renderOption}
        renderInput={params => (
          <TextField
            {...params}
            name={item?._id}
            value={inputValue?.toString() || ''}
            type="number"
            onWheel={e => e.target.blur()}
            onBlur={e => {
              // console.log('onBlur e: ');

              setFocus(false);
            }}
            onFocus={e => {
              // console.log('onFocus e: ', name);
              setFocus(true);
            }}
            onKeyPress={event => {
              if (event.key === 'Enter') {
                saveEditValue();
              }
            }}
            onKeyDown={e => symbolsArr.includes(e.key) && e.preventDefault()}
            placeholder={placeholder || 'Enter an Amount'}
            inputRef={inputRef}
            label={label || 'Change Amount'}
            error={!!inputError}
            FormHelperTextProps={{
              sx: { color: inputError ? 'error.main' : 'text.primary' },
            }}
            helperText={inputError || ''}
            InputProps={{
              ...params.InputProps,
              inputProps: {
                ...params.inputProps,
                type: 'number',
                autoComplete: 'new-password', // disable autocomplete and autofill
                step: '0.01',
                min: least,
                max: highest,
              },
              endAdornment,
            }}
            sx={{ backgroundColor: 'transparent !important' }}
          />
        )}
      />
      {!hideUpdateButton &&
        inputValue !== selectValue &&
        !onlyAllowFixedPartialPremiumAmount &&
        !onlyAllowFixedPartialLoanAmount && (
          <Button
            onClick={() => saveEditValue()}
            variant="contained"
            size="small"
            sx={{ width: 'auto' }}
          >
            Update
          </Button>
        )}
    </Stack>
  );
}
